import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import { Loader2, Mail, MessageSquare, Phone } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UnsubscribeModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    unsubscribe_email: true,
    unsubscribe_sms: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email && !formData.phone) {
      toast.error('Please enter your email address or phone number');
      return;
    }

    if (!formData.unsubscribe_email && !formData.unsubscribe_sms) {
      toast.error('Please select at least one option to unsubscribe');
      return;
    }

    if (formData.unsubscribe_sms && !formData.phone) {
      toast.error('Please enter your phone number to unsubscribe from SMS');
      return;
    }

    if (formData.unsubscribe_email && !formData.email) {
      toast.error('Please enter your email to unsubscribe from email communications');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/unsubscribe`, formData);
      toast.success('You have been successfully unsubscribed');
      setFormData({ email: '', phone: '', unsubscribe_email: true, unsubscribe_sms: false });
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to unsubscribe. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0F172A] border border-[#1E293B] shadow-2xl shadow-black/50 max-w-md rounded-none" data-testid="unsubscribe-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-slate-50" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Unsubscribe
          </DialogTitle>
        </DialogHeader>

        <p className="text-slate-500 text-sm mt-2">
          We're sorry to see you go. Please enter your details and select which communications you'd like to unsubscribe from.
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
          <div>
            <Label htmlFor="unsub-email" className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
              Email Address {formData.unsubscribe_email && <span className="text-red-400">*</span>}
            </Label>
            <Input
              id="unsub-email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              className="bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 rounded-none"
              placeholder="you@example.com"
              data-testid="unsubscribe-email-input"
            />
          </div>

          <div>
            <Label htmlFor="unsub-phone" className="text-xs uppercase tracking-widest text-slate-500 mb-2 block">
              Phone Number {formData.unsubscribe_sms && <span className="text-red-400">*</span>}
            </Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" strokeWidth={1.5} />
              <Input
                id="unsub-phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                className="pl-10 bg-[#020617] border-[#1E293B] focus:border-[#D4AF37] text-slate-100 placeholder:text-slate-600 rounded-none"
                placeholder="+1 555 123 4567"
                data-testid="unsubscribe-phone-input"
              />
            </div>
          </div>

          <div className="space-y-3">
            <Label className="text-xs uppercase tracking-widest text-slate-500 block">
              Unsubscribe From
            </Label>
            
            <div className="flex items-start space-x-3 p-3 bg-[#020617] rounded-none border border-[#1E293B]">
              <Checkbox
                id="unsubscribe_email"
                checked={formData.unsubscribe_email}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, unsubscribe_email: checked }))}
                className="mt-0.5 border-[#334155] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                data-testid="unsubscribe-email-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="unsubscribe_email" className="text-sm text-slate-200 cursor-pointer flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  Email Communications
                </Label>
                <p className="text-xs text-slate-600 mt-1">
                  Newsletter, promotional emails, and updates
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3 p-3 bg-[#020617] rounded-none border border-[#1E293B]">
              <Checkbox
                id="unsubscribe_sms"
                checked={formData.unsubscribe_sms}
                onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, unsubscribe_sms: checked }))}
                className="mt-0.5 border-[#334155] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                data-testid="unsubscribe-sms-checkbox"
              />
              <div className="flex-1">
                <Label htmlFor="unsubscribe_sms" className="text-sm text-slate-200 cursor-pointer flex items-center">
                  <MessageSquare className="w-4 h-4 mr-2 text-[#D4AF37]" strokeWidth={1.5} />
                  SMS Communications
                </Label>
                <p className="text-xs text-slate-600 mt-1">
                  Text messages and SMS alerts
                </p>
              </div>
            </div>
          </div>

          <div className="bg-[#020617] border border-[#1E293B] rounded-none p-3">
            <p className="text-[10px] text-slate-600 leading-relaxed">
              By submitting this form, you will be removed from our selected mailing lists. 
              Please allow up to 48 hours for your request to be processed. You may still receive 
              transactional emails related to your account. You can also text STOP to any SMS message to unsubscribe immediately.
            </p>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-[#020617] hover:bg-[#B8941F] font-bold uppercase tracking-widest py-2 rounded-none"
            data-testid="unsubscribe-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Unsubscribe'
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
